import React from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { DeviceRow } from './DeviceRow';
import { Device } from '../../../../device/types/device';

const noop = (_device: Device) => {};

const StationCard: React.FC<{
  stationNumber: string;
  devices: Device[];
  onOpenWeldParameters?: () => void;
}> = ({ stationNumber, devices, onOpenWeldParameters }) => {
  // Each station typically has 1–2 devices, but we render however many exist.
  const columnClass =
    devices.length >= 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1';

  return (
    <Card className="border border-[#E2E5EA] shadow-sm overflow-hidden">
      <CardContent className="p-0">
        <div className="px-4 py-2.5 border-b border-[#E2E5EA] bg-page-bg flex items-baseline gap-2">
          <h3 className="text-[13px] font-semibold text-blueprint-blue">
            Station {stationNumber}
          </h3>
          <span className="text-[11px] text-heritage-grey">
            {devices.length} device{devices.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className={`grid ${columnClass} divide-y md:divide-y-0 md:divide-x divide-[#E2E5EA]`}>
          {devices.map(device => (
            <DeviceRow
              key={device.id}
              device={device}
              onManage={noop}
              onStatusUpdate={noop}
              embedded
              orientation={device.location?.parsed?.BugType || ''}
              onOpenWeldParameters={onOpenWeldParameters}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const StationWall: React.FC<{ devices: Device[]; onOpenWeldParameters?: () => void }> = ({ devices, onOpenWeldParameters }) => {
  // Separate devices that have a station assignment from those that don't.
  const devicesWithLocation = devices.filter(d => d.location?.parsed?.Station);
  const devicesWithoutLocation = devices.filter(d => !d.location?.parsed?.Station);

  // Group ALL devices belonging to the same station — there may be more than one
  // device per orientation at a given station, so we keep them as an array.
  const byStation = new Map<string, Device[]>();
  for (const device of devicesWithLocation) {
    const station = String(device.location?.parsed?.Station);
    if (!byStation.has(station)) byStation.set(station, []);
    byStation.get(station)!.push(device);
  }

  // Sort stations numerically (falls back to string compare for non-numeric ids).
  const stations = Array.from(byStation.entries()).sort(([a], [b]) => {
    const an = Number(a);
    const bn = Number(b);
    if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
    return a.localeCompare(b);
  });

  return (
    <div className="w-full">
      <div className="mb-5 flex items-baseline gap-2">
        <h2 className="text-[16px] font-semibold text-blueprint-blue">Devices</h2>
        <span className="text-[13px] text-heritage-grey">{devices.length} total</span>
      </div>

      {stations.length > 0 && (
        <div className="mb-6">
          <h3 className="text-[13px] font-medium text-text-grey mb-3">Stations</h3>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {stations.map(([stationNumber, stationDevices]) => (
              <StationCard
                key={stationNumber}
                stationNumber={stationNumber}
                devices={stationDevices}
                onOpenWeldParameters={onOpenWeldParameters}
              />
            ))}
          </div>
        </div>
      )}

      {devicesWithoutLocation.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-[13px] font-medium text-text-grey">
            Devices without Station Assignment
            <span className="text-[12px] text-hero-red ml-2">
              ({devicesWithoutLocation.length} device{devicesWithoutLocation.length === 1 ? '' : 's'})
            </span>
          </h3>
          {devicesWithoutLocation.map(device => (
            <DeviceRow
              key={device.id}
              device={device}
              onManage={noop}
              onStatusUpdate={noop}
              stationLabel="Location Not Available"
              unassigned
              onOpenWeldParameters={onOpenWeldParameters}
            />
          ))}
        </div>
      )}

      {devices.length === 0 && (
        <div className="text-center py-12">
          <div className="text-heritage-grey text-[16px] mb-2">No devices found</div>
          <div className="text-heritage-grey text-[13px]">
            No devices are currently assigned to this project.
          </div>
        </div>
      )}
    </div>
  );
};

export default StationWall;
